import { useFormContext } from 'react-hook-form'
import { NavigationView } from '../../layout/NavigationView'
import {
    ColoursObj,
    Gutters,
    MobileConfirmDialog,
    TDialogStore,
    useDialogStore,
} from '@eventr/common-storybook'
import { Stack, Box } from '@mui/material'
import {
    editHeaderTranslation,
    editItineraryFieldsTranslation,
    editItineraryTranslation,
    generalTranslation,
    homeTranslation,
} from '../../../i18n/translateHelpers'
import { ToolbarItemProps } from '../../layout/TopToolbar'
import { bs, useBoundStore } from '../../../state/bound.store'
import { selectCurrentItineraryTitle } from '../../../state/itineraryState/currentItin.slice'
import { loadingSignal } from '../../../state/overlayState/overlaySignals'
import { useCallback, useMemo } from 'react'
import i18n from '../../../i18n/i18next'
import { useShallow } from 'zustand/react/shallow'
import { SectionEdit } from '../formComponents/section'
import { FormText } from '../formComponents/formText'
import { FormDateRange } from '../formComponents/formDateRange'
import { FormAddressInput } from '../formComponents/formAddressInput'
import { TimezoneSelectorForm } from '../formComponents/formTimezoneSelector'
import { PremiumFormUploadDocs } from '../formComponents/formUploadDocs'
import { PremiumAppFormImageSelect } from '../formComponents/formImageSelect'
import { useGlobalRouter } from '../../globalRouter/useGlobalRouter'
import { defaultAddSnack } from '../../../state/overlayState/overlayContext'

export const EventInfoForm = () => {
    const { formState, handleSubmit, control } = useFormContext()
    const { goBack } = useGlobalRouter()
    const { isDirty } = formState
    const { replaceDialog, clearDialog }: TDialogStore = useDialogStore(
        useShallow((s: TDialogStore) => ({
            clearDialog: s.clearDialog,
            replaceDialog: s.replaceDialog,
        }))
    )

    const itineraryTitle = useBoundStore(selectCurrentItineraryTitle)
    // get new from url params
    const newItem = new URLSearchParams(window.location.search).get('new')
    const isNew = newItem === 'true'
    const onSaveData = async () => {
        try {
            await handleSubmit(async (data) => {
                try {
                    loadingSignal.value = true
                    let resp
                    if (isNew) {
                        resp = await bs().createItinerary(data)
                    } else {
                        resp = await bs().updateItinerarySection(1, data)
                    }
                    // const res = await bs().upsertElement(8, data)
                    if (resp) {
                        goBack()
                    }
                } catch (error) {
                    console.error('Error saving itinerary:', error)
                    defaultAddSnack(
                        'Failed to create itinerary. Please make sure all details entered are correct.',
                        'error'
                    )
                } finally {
                    loadingSignal.value = false
                }
            })()
        } catch (error) {
            defaultAddSnack(
                'Failed to create itinerary. Please make sure all details entered are correct.',
                'error'
            )
            // Handle error
        }
    }
    const handleDialogConfirm = useCallback(() => {
        clearDialog()

        window.goBack()
    }, [clearDialog])
    const onBackPressed = useCallback(() => {
        if (isDirty) {
            replaceDialog({
                node: (
                    <MobileConfirmDialog
                        text={i18n.t(
                            'edit-itinerary.item.unsaved-booking-text'
                        )}
                        header={i18n.t('edit-itinerary.item.unsaved-booking')}
                        onCancel={() => {
                            clearDialog()
                        }}
                        onConfirm={handleDialogConfirm}
                        cancelText={generalTranslation('cancel')}
                        confirmText={generalTranslation('leave')}
                    />
                ),
                backdropClosable: false,
            })
        } else {
            window.goBack()
        }
    }, [isDirty, replaceDialog, clearDialog, handleDialogConfirm])

    const backButton: ToolbarItemProps = {
        id: 'backButtonNavigation',
        buttonText: generalTranslation('cancel'),
        useButton: true,
        colour: ColoursObj.core.primary,
        action: () => {
            onBackPressed()
        },
    }
    const headerText = useMemo(() => {
        return isNew
            ? homeTranslation('New Itinerary')
            : generalTranslation('Edit')
    }, [isNew])
    return (
        <>
            <NavigationView
                usePageTransition
                navigationBarTitle={headerText}
                navigationBarSubtitle={isNew ? undefined : itineraryTitle}
                backButton={backButton}
                backButtonAction={onBackPressed}
                navigationBarTrailing={[
                    {
                        id: 'saveButton',
                        disabled: !isDirty,
                        buttonText: generalTranslation('done'),
                        colour: ColoursObj.core.primary,
                        useButton: true,
                        action: () => {
                            onSaveData()
                        },
                    },
                ]}
            >
                <Gutters>
                    <Stack gap="16px">
                        <Stack spacing="16px">
                            <SectionEdit
                                headerIcon="Itinerary"
                                headerText={editHeaderTranslation(
                                    'Itinerary Details'
                                )}
                            />
                            <FormText
                                required
                                style={{ marginTop: '16px' }}
                                fieldLabel={editItineraryFieldsTranslation('1')}
                                control={control}
                                name="title"
                            />
                            <FormDateRange
                                control={control}
                                endName="end_date"
                                startName="start_date"
                                required
                                fieldLabel={editItineraryFieldsTranslation(
                                    '11'
                                )}
                            />
                            <FormAddressInput
                                fieldLabel={editItineraryFieldsTranslation('4')}
                                control={control}
                                name="OSMAddress"
                            />
                            <TimezoneSelectorForm
                                name="OSMAddress.timeZone"
                                control={control}
                            />
                            <FormText
                                fieldLabel={editItineraryFieldsTranslation('5')}
                                control={control}
                                name="website"
                            />
                            <PremiumFormUploadDocs
                                fieldLabel={editItineraryFieldsTranslation(
                                    'Documents'
                                )}
                                control={control}
                                name="additionFiles"
                            />
                            <PremiumAppFormImageSelect
                                name="cover_image"
                                control={control}
                                height="80px"
                                label={editItineraryFieldsTranslation(
                                    'Cover Image'
                                )}
                                helperText={
                                    editItineraryTranslation(
                                        'Recommended ratio'
                                    ) + ' 1:5'
                                }
                            />
                            <FormText
                                fieldLabel={editItineraryFieldsTranslation(
                                    '10'
                                )}
                                control={control}
                                name="additionalInfo"
                                multiline
                                rows={2}
                            />
                        </Stack>
                        <Box height={'16px'} />
                    </Stack>
                </Gutters>
            </NavigationView>
        </>
    )
}
